import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError } from "@pontimed/api-client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { RefreshCw, TriangleAlert } from "lucide-react";

import { LiveSwitchCard, type LiveEnvelope } from "@/components/admin/marketing/live-switch-card";
import {
  adsLiveQueryKey,
  adsLiveQueryOptions,
  marketingSettingsQueryKey,
  marketingSettingsQueryOptions,
  readinessQueryKey,
  readinessQueryOptions,
  socialLiveQueryKey,
  socialLiveQueryOptions,
  type MarketingSettingsEnvelope,
} from "@/components/admin/marketing/readiness/live-queries";
import {
  ReadinessChecklist,
  ReadinessChecklistSkeleton,
  ReadinessSummaryStrip,
} from "@/components/admin/marketing/readiness/readiness-checklist";
import { Button } from "@/components/ui/button";
import { useAdminContext } from "@/lib/admin-context";

/**
 * Admin → Marketing → Readiness — the single go-live screen.
 *
 * Top: the three delivery switches (direct marketing, social publishing, paid
 * ads), each showing both layers — the environment ceiling pontimed-workers
 * reported and the audited admin switch. Below: every check apps/api runs
 * before real messages may leave, grouped (delivery, legal, automation, ads,
 * social, infra), worst news summarised in one strip.
 *
 * A green checklist sends nothing on its own; only the switches do.
 *
 * Endpoints:
 *   GET /api/admin/marketing/readiness     → { checks: [{ id, group, status, detail, fix }] }
 *   GET /api/admin/marketing/settings      → { settings, live, workerEnv, effectiveLive }
 *   PUT /api/admin/marketing/live          { enabled, confirm? }
 *   GET /api/admin/social/settings         → { …, live, workerEnv, effectiveLive }
 *   PUT /api/admin/social/settings/live    { enabled, confirm? }
 *   GET|PUT /api/admin/ads/live            same envelope
 *
 * Every query is keyed on `dbTarget` (see live-queries.ts).
 */

export const Route = createFileRoute("/_authenticated/marketing-readiness")({
  component: MarketingReadinessPage,
});

function errorText(error: unknown): string {
  if (error instanceof ApiError) return `HTTP ${error.status}`;
  return error instanceof Error ? error.message : String(error);
}

function MarketingReadinessPage() {
  const { t } = useLingui();
  const { dbTarget } = useAdminContext();
  const queryClient = useQueryClient();

  const readiness = useQuery(readinessQueryOptions(dbTarget));
  const marketing = useQuery(marketingSettingsQueryOptions(dbTarget));
  const social = useQuery(socialLiveQueryOptions(dbTarget));
  const ads = useQuery(adsLiveQueryOptions(dbTarget));

  const running =
    readiness.isFetching || marketing.isFetching || social.isFetching || ads.isFetching;

  const runAgain = () => {
    void readiness.refetch();
    void marketing.refetch();
    void social.refetch();
    void ads.refetch();
  };

  // A switch flip changes what the "live" checks report — re-run them.
  const rerunChecks = () => {
    void queryClient.invalidateQueries({ queryKey: readinessQueryKey(dbTarget) });
  };

  const onMarketingChanged = (next: LiveEnvelope) => {
    queryClient.setQueryData<MarketingSettingsEnvelope>(
      marketingSettingsQueryKey(dbTarget),
      (prev) => (prev ? { ...prev, live: next } : { settings: [], live: next }),
    );
    rerunChecks();
  };

  const onSocialChanged = (next: LiveEnvelope) => {
    queryClient.setQueryData(socialLiveQueryKey(dbTarget), next);
    rerunChecks();
  };

  const onAdsChanged = (next: LiveEnvelope) => {
    queryClient.setQueryData(adsLiveQueryKey(dbTarget), next);
    rerunChecks();
  };

  const generatedAt = readiness.data?.generatedAt
    ? new Date(readiness.data.generatedAt).toLocaleString()
    : null;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-foreground text-2xl font-bold tracking-tight">
            <Trans>Go-live readiness</Trans>
          </h1>
          <p className="text-muted-foreground text-sm">
            <Trans>
              Everything that must be true before real messages leave — and the three switches that
              let them.
            </Trans>
          </p>
        </div>
        <Button
          variant="outline"
          className="min-h-11"
          onClick={runAgain}
          disabled={running}
          aria-label={t`Run the readiness checks again`}
        >
          <RefreshCw className={running ? "size-4 animate-spin" : "size-4"} aria-hidden />
          <Trans>Run again</Trans>
        </Button>
      </header>

      <section aria-labelledby="readiness-switches-heading" className="space-y-3">
        <div>
          <h2 id="readiness-switches-heading" className="text-lg font-semibold">
            <Trans>Delivery switches</Trans>
          </h2>
          <p className="text-muted-foreground text-xs">
            <Trans>
              Something leaves only when both layers are open: the environment ceiling set on
              pontimed-workers and the admin switch here. Every switch starts off — nothing leaves
              until someone turns it on.
            </Trans>
          </p>
        </div>
        <div className="space-y-4">
          <LiveSwitchCard
            key={`marketing:${dbTarget}`}
            kind="marketing"
            endpoint="/api/admin/marketing/live"
            envelope={marketing.data?.live ?? null}
            loading={marketing.isPending}
            onChanged={onMarketingChanged}
          />
          <LiveSwitchCard
            key={`social:${dbTarget}`}
            kind="social"
            endpoint="/api/admin/social/settings/live"
            envelope={social.data ?? null}
            loading={social.isPending}
            onChanged={onSocialChanged}
          />
          <LiveSwitchCard
            key={`ads:${dbTarget}`}
            kind="ads"
            endpoint="/api/admin/ads/live"
            envelope={ads.data ?? null}
            loading={ads.isPending}
            onChanged={onAdsChanged}
          />
        </div>
      </section>

      <section aria-labelledby="readiness-checks-heading" className="space-y-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 id="readiness-checks-heading" className="text-lg font-semibold">
            <Trans>Checks</Trans>
          </h2>
          {generatedAt ? (
            <span className="text-muted-foreground text-xs">
              <Trans>Last run: {generatedAt}</Trans>
            </span>
          ) : null}
        </div>

        {readiness.isError ? (
          <div className="border-destructive-border bg-destructive-soft flex items-start gap-3 rounded-lg border p-4">
            <TriangleAlert className="text-destructive-strong size-5 shrink-0" aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="text-destructive-strong text-sm font-semibold">
                <Trans>The readiness checks could not run</Trans>
              </p>
              <p className="text-destructive-strong/90 mt-1 font-mono text-xs break-words">
                {errorText(readiness.error)}
              </p>
            </div>
          </div>
        ) : (
          <>
            <ReadinessSummaryStrip report={readiness.data ?? null} loading={readiness.isPending} />
            {readiness.isPending ? (
              <ReadinessChecklistSkeleton />
            ) : readiness.data ? (
              <ReadinessChecklist report={readiness.data} />
            ) : null}
          </>
        )}
      </section>
    </div>
  );
}
